import {buildStreetWithHouseNumber, type GeoRefAddress} from "@reva/shared/geo-reference";

/** Ein externer Kartendienst, zu dem aus REVA heraus gesprungen werden kann. */
export type ExternalMapProvider = "google" | "bing" | "osm";

/** Reihenfolge, in der die Absprung-Icons dargestellt werden. */
export const EXTERNAL_MAP_PROVIDERS: ExternalMapProvider[] = ["google", "bing", "osm"];

/** Bing öffnet damit die 3D-Ansicht mit Luftbildern statt der Straßenkarte; der Pin aus `q` bleibt. */
const BING_3D_STYLE = "style=3d";

/**
 * Baut aus einer georeferenzierten Adresse den Suchbegriff für einen externen Kartendienst.
 * Bevorzugt die strukturierten Felder (Straße, PLZ, Gemeinde), weil die Anzeigenamen Zusätze
 * enthalten können, mit denen fremde Geocoder schlecht umgehen. Fallback ist der Anzeigename.
 * @param addressMeta die Adresse
 * @return Suchbegriff, ggf. leer, wenn die Adresse keine brauchbaren Felder hat
 */
export function buildAddressQuery(addressMeta: GeoRefAddress | null | undefined): string {
    if (!addressMeta) return "";
    const cityLine = [addressMeta.postcode, addressMeta.municipality].filter(Boolean).join(" ");
    const street = buildStreetWithHouseNumber(addressMeta.street, addressMeta.houseNumber);
    const query = [street, cityLine].filter(Boolean).join(", ");
    return query || addressMeta.displayNameDE || addressMeta.displayNameEN || "";
}

/**
 * URL, die die Adresse als Suchbegriff an den externen Kartendienst übergibt.
 * @param provider der Kartendienst
 * @param query der Suchbegriff (unkodiert)
 * @return die URL oder null, wenn kein Suchbegriff vorliegt
 */
export function buildAddressMapUrl(provider: ExternalMapProvider, query: string): string | null {
    const trimmed = (query ?? "").trim();
    if (!trimmed) return null;
    const q = encodeURIComponent(trimmed);
    switch (provider) {
        case "google":
            return `https://www.google.com/maps/search/?api=1&query=${q}`;
        case "bing":
            return `https://www.bing.com/maps?q=${q}&${BING_3D_STYLE}`;
        case "osm":
            return `https://www.openstreetmap.org/search?query=${q}`;
    }
}

/**
 * URL, die die Koordinate an den externen Kartendienst übergibt.
 * Die Koordinaten werden immer unformatiert (Punkt als Dezimaltrenner) übergeben.
 * @param provider der Kartendienst
 * @param lat Breitengrad
 * @param lon Längengrad
 * @return die URL oder null, wenn keine gültige Koordinate vorliegt
 */
export function buildCoordinateMapUrl(
    provider: ExternalMapProvider,
    lat: number | null | undefined,
    lon: number | null | undefined,
): string | null {
    if (typeof lat !== "number" || typeof lon !== "number") return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    switch (provider) {
        case "google":
            return `https://www.google.com/maps/search/?api=1&query=${lat}%2C${lon}`;
        case "bing":
            // Bing setzt nur einen Pin, wenn die Koordinate als Suchbegriff kommt: `cp`/`lvl`
            // zentriert die Karte lediglich, und der alte Pushpin-Parameter `sp=point....`
            // wird ignoriert. Beides 2026-08-21 gegen www.bing.com/maps geprüft.
            return `https://www.bing.com/maps?q=${encodeURIComponent(`${lat}, ${lon}`)}&${BING_3D_STYLE}`;
        case "osm":
            return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`;
    }
}
